// src/shared/bug-reporter/hooks/useScreenshotCapture.ts
// Captures the current page using modern-screenshot. Returns a base64 JPEG data
// URL (compressed for transport). Capture is fired explicitly at modal open
// time, before the dimming backdrop is rendered — see ReporterButton.
//
// Why modern-screenshot and not html2canvas: html2canvas re-parses CSS itself
// and throws on modern color functions, so screenshots failed on every Tailwind
// v4 consumer (Tailwind v4 emits oklch() everywhere) — the modal showed
// "Screenshot unavailable". modern-screenshot clones the DOM into an SVG
// <foreignObject> and lets the browser resolve computed styles, so oklch() and
// other modern CSS render natively.

import { useCallback, useState } from 'react'
import { domToJpeg } from 'modern-screenshot'
import * as Sentry from '@sentry/react'

/**
 * What transparent pixels become. JPEG has no alpha channel, so the encoder
 * needs something to flatten onto; without it, transparency renders black and
 * every screenshot of a light UI comes back with black gutters.
 *
 * Deliberately NOT a design token, which is what the token lint's hint suggests
 * (ops-panel#81). It is an encoder parameter, not a surface colour, and this
 * directory is copied into apps whose background token is not necessarily
 * white. Naming it is the fix; tokenising it would be wrong.
 */
const JPEG_FLATTEN_COLOR = '#ffffff'

/**
 * Draws an input marked `data-screenshot-mask` as a password box in the
 * screenshot, even while the page shows its value in the clear.
 *
 * Why (admin-frontend #1106): the Admin employee portal's federal forms let an
 * employee tap "Show" on a Social Security, A-, I-94 or passport number they
 * are typing, and on a confirmation password. modern-screenshot copies each
 * input's value into its clone, and the screenshot is uploaded with the
 * report, so a report filed in that moment carried the number in the clear.
 * It runs on the CLONE (`onCloneEachNode`): the live page is untouched. Any
 * consumer marks a revealable field with the attribute; nothing else changes.
 */
export function maskSensitiveInputs(cloned: Node): void {
  if (cloned instanceof HTMLInputElement && cloned.hasAttribute('data-screenshot-mask')) {
    cloned.setAttribute('type', 'password')
  }
}

interface CaptureResult {
  /** JPEG data URL; empty string if capture failed. */
  dataUrl: string
  error: string | null
}

/**
 * The message every capture failure is grouped under in Sentry. A constant so
 * the events aggregate into one issue whose COUNT is the signal — a spike is
 * what "capture is broken for everyone" looks like, and the per-event
 * `captureError` / `app` fields are what say where.
 */
export const SCREENSHOT_FAILURE_EVENT = 'Bug reporter: screenshot capture failed'

/**
 * Why this exists (customer#168).
 *
 * The soft fallback below is CORRECT and is kept: `design-doc.md:433` specifies
 * "auto-captured screenshot field includes alt-text fallback in case rendering
 * fails", and a reporter mid-bug-report should not be blocked by a rasteriser.
 * It is the wrong thing to be the ONLY signal for a *systemic* failure.
 * customer#156 was a 100% capture-failure rate on Customer Web that survived
 * ~3 months undetected, and it was invisible precisely because it degraded
 * gracefully and submitted anyway: nothing failed, so nothing was investigated.
 *
 * Note the asymmetry that makes a human an unreliable detector here — the field
 * is labelled "Auto-captured (no action needed)" and every other row in it is
 * populated, so a reporter skimming the list has no reason to notice the one
 * missing row and no idea it matters.
 *
 * So: keep the fallback, add a signal. One warning-level Sentry event per
 * failure, carrying the error and the host app name. That alone would have
 * surfaced customer#156 within a day, and it needs no backend contract change.
 *
 * ON THE DEPENDENCY. `@sentry/react` is a runtime dependency of every current
 * consumer of this directory (palate-ops `^10.74.0`, palate-customer-app
 * `^10.74.0`), and both call `Sentry.init`. A consumer that has the SDK but has
 * not initialised it gets a documented no-op, so this degrades safely there.
 * A future consumer that lacks the package gets a BUILD error — which is the
 * failure mode we want, and the reason this is a static import rather than the
 * `focus.ts`-style host-neutral constant: `focus.ts` avoids a semantic token
 * because an undefined token degrades SILENTLY to no ring at all. Here silence
 * is the bug being fixed, so a loud failure is strictly better than a graceful
 * one. A new consumer must add `@sentry/react`; see SYNC.md.
 */
function reportCaptureFailure(appName: string, captureError: string): void {
  try {
    Sentry.captureMessage(SCREENSHOT_FAILURE_EVENT, {
      level: 'warning',
      tags: { component: 'bug-reporter', app: appName },
      extra: { captureError },
    })
  } catch {
    // Telemetry must never be the thing that breaks the bug reporter. If the
    // SDK is missing, misconfigured or throwing, the capture fallback below
    // still runs and the report still submits — exactly as before this change.
  }
}

/**
 * @param appName Host app identifier, the same value submitted as auto-capture
 *   `app`. Required rather than optional so a new consumer cannot wire the
 *   widget up and silently lose the attribution on the telemetry.
 */
export function useScreenshotCapture(appName: string) {
  const [isCapturing, setIsCapturing] = useState(false)
  const [last, setLast] = useState<CaptureResult | null>(null)

  const capture = useCallback(async (): Promise<CaptureResult> => {
    setIsCapturing(true)
    try {
      // Cap devicePixelRatio at 2 and compress to JPEG @ 0.8 to keep the
      // multipart payload small.
      const dataUrl = await domToJpeg(document.body, {
        quality: 0.8,
        backgroundColor: JPEG_FLATTEN_COLOR,
        scale: Math.min(window.devicePixelRatio || 1, 2),
        onCloneEachNode: maskSensitiveInputs,
      })
      const result: CaptureResult = { dataUrl, error: null }
      setLast(result)
      return result
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Screenshot capture failed'
      // Signal first, then degrade. The fallback below is unchanged — the
      // report still submits with an empty screenshot and the modal still shows
      // "Screenshot unavailable" — but the failure is no longer invisible.
      reportCaptureFailure(appName, message)
      const result: CaptureResult = { dataUrl: '', error: message }
      setLast(result)
      return result
    } finally {
      setIsCapturing(false)
    }
  }, [appName])

  return { capture, isCapturing, last }
}
